import { useEffect, useState } from 'react';

import { useFeatureFlag } from '@seek/candidate-feature-flag';

import {
  LaunchDarklyFeatureKeys,
  type LaunchDarklyFeatureFlagMap,
} from '../constants/launchDarklyFeatureFlags';

/**
 * Hook that handles LaunchDarkly feature flags with timeout fallback.
 *
 * Five main scenarios:
 * 1. Cookie Override: variant = expectedValues (immediate)
 * 2. LaunchDarkly before timeout: variant = valueFromLaunchDarkly (quick response)
 * 3. Timeout: variant = '1.0.0' (fallback after timeoutMs)
 * 4. LaunchDarkly after timeout: variant = '1.0.0' (ignores late response)
 * 5. Unexpected LaunchDarkly value: variant = '1.0.0' (safety fallback)
 *
 * Usage in component useEffect:
 * - For tracking/business logic: use [variant] or [variantWithPrefix] - triggers once per scenario
 * - For UI updates/loading states: use [isLoading] - immediate UI feedback
 */
export const useOverridableFeatureFlag = (
  key: LaunchDarklyFeatureKeys,
  expectedValues: string,
  isOverrideByCookie: boolean,
  timeoutMs: number = 1000,
) => {
  const valueFromLaunchDarkly =
    useFeatureFlag<LaunchDarklyFeatureFlagMap[typeof key]['values'][number]>(
      key,
    );

  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [variant, setVariant] = useState<string | undefined>(undefined);

  // First useEffect - Handles Scenarios 1, 2, 4, 5 (all response handling)
  useEffect(() => {
    // If already timed out, ignore any late LaunchDarkly responses (Scenario 4)
    if (hasTimedOut && !isOverrideByCookie) {
      return;
    }

    // Handle immediate responses
    if (valueFromLaunchDarkly || isOverrideByCookie) {
      if (isOverrideByCookie) {
        setVariant(expectedValues);
      } else {
        // Validate LaunchDarkly value - use fallback for unexpected values
        const isValidValue = valueFromLaunchDarkly === expectedValues;
        setVariant(isValidValue ? valueFromLaunchDarkly : '1.0.0');
      }
    }
  }, [hasTimedOut, valueFromLaunchDarkly, isOverrideByCookie, expectedValues]);

  // Second useEffect - Handles Scenario 3 (timeout setup)
  useEffect(() => {
    // Early return - Don't set timeout if we already have a value or cookie override
    if (valueFromLaunchDarkly || isOverrideByCookie) {
      return;
    }

    // Reset timeout state since we need to wait for LaunchDarkly response
    setHasTimedOut(false);

    const timeout = setTimeout(() => {
      setHasTimedOut(true);
      setVariant('1.0.0'); // Set fallback value and stop listening
    }, timeoutMs);

    return () => clearTimeout(timeout);
  }, [timeoutMs, valueFromLaunchDarkly, isOverrideByCookie]);

  const isExperimentGroup = isOverrideByCookie || variant === expectedValues;

  const variantWithPrefix = variant ? `schema_v${variant}` : null;

  const isLoading =
    !valueFromLaunchDarkly && !isOverrideByCookie && !hasTimedOut;

  return {
    isExperimentGroup,
    variant,
    variantWithPrefix,
    isLoading,
  };
};
